import type { QuizBlock } from '@/types/blocks'
import { useStrictLang } from './design/strictLangContext'
import type { ContentLang } from '@/features/editor/editorSlice'
import { QuizShell, useFitLines, blockTheme } from '@teach/ui'
import { selectedOptions, toggleOption } from '../../preview/answerState'
import { answerableOptions } from '../../preview/quizOptions'
import type { PreviewRun } from '../../preview/previewRun'
import { getDisplayBody } from '../quizBodySync'
import { AnswerFeedback } from './AnswerFeedback'
import { FillBlankChip } from './FillBlankChip'
import { fillPillStyle, optionState, verdictState } from './optionAppearance'
import { PreviewShell } from './PreviewShell'
import { cn } from '@/lib/utils'
import { useFitChips } from './useFitChips'
import { useQuizMascot } from './quizMascotContext'
import { quizFeedback } from './quizFeedback'
import { splitFillBlank } from './fillBlankSplit'
import { pickMessages } from '@/i18n'
import { messages } from './messages'

interface Props {
  block: QuizBlock
  contentLang: ContentLang
  /** Прохождение: слова становятся кликабельными, выбранное встаёт в пропуск. */
  run?: PreviewRun
}

// Утверждение делится по первому `___`, слово из списка встаёт в пропуск.
// `hasGap` (порт плеерного `splitBlank`) решает, рисовать ли пилюлю слота
// вовсе — без гэпа (пустое/малоформное утверждение) слота у ученика нет.
export const FillBlankQuizPreview = ({ block, contentLang, run }: Props) => {
  // Экран ученика: кнопки и заголовки — на языке курса, как в плеере.
  const p = pickMessages(messages, contentLang)
  // Холст и миниатюры редактора — строго язык правки; прохождение — как плеер.
  const strictLang = useStrictLang() && (!run || run.onCanvas === true)
  const mascot = useQuizMascot(run?.verdict)
  const rawQuestion = getDisplayBody(block, contentLang, strictLang).question
  // Пустое утверждение — автору серая подсказка-пример (как в панели), ученику — ничего.
  const isPlaceholder = (!run || Boolean(run.onCanvas)) && !rawQuestion
  const question = isPlaceholder ? p.fillBlankSample : (rawQuestion ?? '')
  const { before, after, hasGap } = splitFillBlank(question)
  // Один список на превью и на проверку: индекс в нём — из ИСХОДНОГО списка
  // вариантов, иначе выбранное слово сверялось бы с соседним.
  const words = answerableOptions(block, contentLang, strictLang)
  const chosen = run ? selectedOptions(run.answer)[0] : undefined
  // `accepted` («оценки нет») у квиза не бывает — но пропускать вердикт в
  // палитру варианта можно только через общий мэппер, а не совпадением строк.
  const slotState =
    run && run.verdict !== null
      ? verdictState(run.verdict)
      : chosen === undefined
        ? 'idle'
        : 'selected'

  // Не больше 3 строк: текст сам уменьшается, в том числе когда выбрано длинное слово.
  const chosenWord = words.find((w) => w.index === chosen)?.text ?? '...'
  const { ref: sentenceRef, fontSize } = useFitLines(`${before}${chosenWord}${after}`, 3, {
    maxPx: 24,
    extraEm: 0.25,
  })

  const { ref: chipsRef, fontSize: chipSize } = useFitChips(words.map((w) => w.text).join('\n'))

  return (
    <PreviewShell
      cta={run?.ctaLabel ?? p.confirm}
      ctaState={run?.ctaState}
      onCta={run?.onCta}
      ctaDisabled={run?.ctaDisabled}
      style={{ backgroundColor: blockTheme.quiz.bg }}
      contentClassName="min-h-0"
      verdictPlate={
        run ? (
          <AnswerFeedback
            lang={contentLang}
            verdict={run.verdict}
            text={quizFeedback(block, contentLang, run.verdict)}
          />
        ) : undefined
      }
    >
      <QuizShell question={p.fillBlankTitle} mascot={mascot} fitMascot>
        <div className="w-full text-center">
          <p
            ref={sentenceRef}
            data-testid="fill-blank-sentence"
            className="mb-[29px] mt-2.5 font-medium leading-[1.25]"
            style={{
              fontSize,
              color: isPlaceholder ? blockTheme.quiz.mutedText : blockTheme.quiz.text,
            }}
          >
            {before}
            {hasGap ? (
              <span
                data-testid="fill-blank-slot"
                // Как в мобилке: пустой пропуск — «...» в строке, заполненный —
                // пилюля 34 без рамки с тенью по состоянию
                className={cn(
                  'mx-1 inline-flex items-center justify-center whitespace-nowrap',
                  chosen === undefined
                    ? 'align-baseline'
                    : 'min-h-[1.42em] rounded-full border-0 px-[0.54em] align-middle',
                )}
                style={chosen === undefined ? undefined : fillPillStyle(slotState)}
              >
                {chosenWord}
              </span>
            ) : null}
            {after}
          </p>
          <div
            ref={chipsRef}
            data-testid="fill-blank-chips"
            className="flex flex-wrap justify-center gap-x-1.5 gap-y-2.5"
            style={{ fontSize: chipSize }}
          >
            {words.map(({ text, index, isCorrect }) => (
              <FillBlankChip
                key={`${text}-${index}`}
                word={text}
                state={run ? optionState(chosen === index, isCorrect, run.verdict) : 'idle'}
                disabled={Boolean(run?.verdict)}
                onSelect={
                  run ? () => run.setAnswer(toggleOption(run.answer, index, false)) : undefined
                }
              />
            ))}
          </div>
        </div>
      </QuizShell>
    </PreviewShell>
  )
}
